import React from "react";
import { Stack } from "@mui/material";
import { usePollIsOpenState } from "../../usePollIsOpenState";
import { PostPollClosedAt, QUIZ_CLOSED_MESSAGE } from "../../PostPollClosedAt";
import { usePollsState } from "../../usePollsState";
import { BasePollType } from "../PostPollFactory";
import { PostPollTextOption } from "../../Options/PostPollTextOption";
import { OptionCircleIcon } from "../../Options/Icons/OptionCircleIcon";
import { CorrectAnswerCheckmark } from "../../Options/Icons/CorrectAnswerCheckmark";
import { CorrectOptionCheckmark } from "../../Options/Icons/CorrectOptionCheckmark";
import { WrongAnswerXMark } from "../../Options/Icons/WrongAnswerXMark";
import { PollOptionType } from "../../../PostForm/PollTab/PollOptionsState";

enum QuizOptionTypeEnum {
    DEFAULT,
    NEUTRAL_OPTION,
    WRONG_ANSWER,
    CORRECT_ANSWER,
    CORRECT_OPTION,
    CORRECT_OPTION_CLOSED,
}

export function PostPollQuizSection({
    pollClosedAt,
    pollOptionHandler,
    pollOptions: initialPollOptions,
    postId,
    answerId,
}: BasePollType & { answerId: PollOptionType["id"] | undefined }) {
    const { open } = usePollIsOpenState(pollClosedAt);
    const {
        totalResponseCount,
        handlePollOptionClick,
        pollOptions,
        hasParticipated,
        userOptionId,
        quizAnswerOptionId,
    } = usePollsState(initialPollOptions, pollOptionHandler, postId);

    answerId = answerId || quizAnswerOptionId;

    const allowVote = open && !hasParticipated;

    return (
        <Stack
            gap={"8px"}
            onClick={(e) => {
                e.stopPropagation();
            }}
        >
            {pollOptions.map((pollOption) => {
                const optionType = getQuizOptionType(
                    pollOption.id,
                    userOptionId,
                    answerId,
                    open,
                );

                const isRespondedByCurrentUser = userOptionId === pollOption.id;
                const { sx, icon } = getOptionProps(
                    optionType,
                    !allowVote && !isRespondedByCurrentUser,
                );
                const votePercentage = totalResponseCount
                    ? Math.round(
                          (pollOption.responsesCount * 100) /
                              totalResponseCount,
                      )
                    : 0;

                return (
                    <PostPollTextOption
                        key={pollOption.id}
                        pollOption={pollOption}
                        handlePollOptionClick={handlePollOptionClick}
                        showDetails={hasParticipated || !open}
                        enableVoting={allowVote}
                        isRespondedByCurrentUser={isRespondedByCurrentUser}
                        optionPercentage={votePercentage}
                        icon={icon}
                        sx={sx}
                    />
                );
            })}
            <PostPollClosedAt
                pollClosedAt={pollClosedAt}
                closedMessage={QUIZ_CLOSED_MESSAGE}
            />
        </Stack>
    );
}

function getQuizOptionType(
    currentOptionId: number,
    userOptionId: number | undefined,
    quizAnswerOptionId: number | undefined,
    open: boolean,
) {
    if (!quizAnswerOptionId) {
        return QuizOptionTypeEnum.DEFAULT;
    }

    if (currentOptionId === quizAnswerOptionId) {
        if (userOptionId === currentOptionId) {
            return QuizOptionTypeEnum.CORRECT_ANSWER;
        }
        if (!open) {
            return QuizOptionTypeEnum.CORRECT_OPTION_CLOSED;
        }
        return QuizOptionTypeEnum.CORRECT_OPTION;
    }

    if (userOptionId !== currentOptionId) {
        return QuizOptionTypeEnum.NEUTRAL_OPTION;
    }

    return QuizOptionTypeEnum.WRONG_ANSWER;
}

function getOptionProps(optionType: QuizOptionTypeEnum, inactive: boolean) {
    switch (optionType) {
        case QuizOptionTypeEnum.CORRECT_OPTION_CLOSED:
            return {
                icon: inactive ? null : (
                    <OptionCircleIcon
                        color={"var(--community-color-transparent-black-50)"}
                    />
                ),
                sx: {
                    background: "var(--community-color-early-dew)",
                    border: "1px solid var(--community-color-early-dew)",
                    " .MuiTypography-root": {
                        color: "var(--community-color-snow-white) !important",
                    },
                },
            };
        case QuizOptionTypeEnum.CORRECT_ANSWER:
            return {
                icon: <CorrectAnswerCheckmark />,
                sx: {
                    background: "var(--community-color-early-dew)",
                    border: "1px solid var(--community-color-early-dew)",
                    " .MuiTypography-root": {
                        color: "var(--community-color-snow-white) !important",
                    },
                },
            };
        case QuizOptionTypeEnum.CORRECT_OPTION:
            return {
                icon: <CorrectOptionCheckmark />,
                sx: {
                    background: "var(--community-color-snow-white)",
                    " .MuiTypography-root": {
                        color: "var(--community-color-early-dew) !important",
                    },
                },
            };
        case QuizOptionTypeEnum.WRONG_ANSWER:
            return {
                icon: <WrongAnswerXMark />,
                sx: {
                    background: "var(--community-color-error-red)",
                    border: "1px solid var(--community-color-error-red)",
                    " .MuiTypography-root": {
                        color: "var(--community-color-snow-white) !important",
                    },
                },
            };
        default:
            return {
                icon: inactive ? null : (
                    <OptionCircleIcon
                        color={"var(--community-color-transparent-black-50)"}
                    />
                ),
                sx: {
                    background: "var(--community-color-snow-white)",
                },
            };
    }
}
